import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import ModalPortal from "../common/ModalPortal";

/**
 * A popover that behaves like one: attached to an anchor, always fully on
 * screen. Portalled to <body> (so no scrolling/clipping ancestor — the
 * page column, the entry modal — can cut it off) and positioned from the
 * anchor's viewport rect:
 *
 *   • below the anchor by default, its right (or left) edge aligned to the
 *     anchor's;
 *   • flips ABOVE when there isn't room below and there is more above;
 *   • if neither side has room for all of it but the viewport does, it is
 *     SHIFTED up (or down) until it is fully visible — covering the anchor
 *     if it must, the way a native menu behaves;
 *   • only when it is taller than the viewport itself does it get a
 *     max-height and scroll inside;
 *   • clamped inside the viewport with an 8px gutter on every side.
 *
 * Re-measures on window resize, on any scroll (capture) and whenever its
 * own size changes (the list arriving after open), and closes on outside
 * click (anchor and popover excluded) and Escape. `anchorRef` is the
 * element the outside-click check and the geometry are taken from.
 */

const GUTTER = 8;
const GAP = 6;

const AnchoredPopover = ({ open, anchorRef, onClose, align, className, style, children, ...rest }) => {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const a = anchorRef.current;
    const p = popRef.current;
    if (!a || !p) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Natural size. When a max-height is in force offsetHeight is the
    // capped box, but scrollHeight is still the content (+2px border) — so
    // nothing has to be mutated to measure, and the ResizeObserver below
    // never sees a change of our own making.
    const w = p.offsetWidth;
    const h = Math.max(p.offsetHeight, p.scrollHeight + 2);

    const roomBelow = vh - GUTTER - (r.bottom + GAP);
    const roomAbove = r.top - GAP - GUTTER;
    const viewport = vh - 2 * GUTTER;

    let top;
    let maxHeight = null;
    if (h <= roomBelow) {
      top = r.bottom + GAP; // the normal case
    } else if (h <= roomAbove) {
      top = r.top - GAP - h; // flip
    } else if (h <= viewport) {
      // Neither side holds it but the screen does: keep it whole and shift
      // it along the axis. Start from the side with more room.
      top = roomBelow >= roomAbove ? vh - GUTTER - h : GUTTER;
    } else {
      // Taller than the screen: pin to the gutters and scroll inside.
      top = GUTTER;
      maxHeight = viewport;
    }

    let left = align === "left" ? r.left : r.right - w;
    left = Math.max(GUTTER, Math.min(left, vw - GUTTER - w));

    const next = { top: Math.max(GUTTER, top), left, maxHeight };
    // Same answer → no state change → no re-render → the observer settles.
    setPos((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight
        ? prev
        : next
    );
  }, [anchorRef, align]);

  // Position synchronously on open so the first paint is already in place.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    place();
    return undefined;
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    // Content can change height after opening (rows landing, a page
    // change); follow it so the popover never grows off the screen.
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && popRef.current) {
      ro = new ResizeObserver(() => place());
      ro.observe(popRef.current);
    }
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      if (ro) ro.disconnect();
    };
  }, [open, place, onClose, anchorRef]);

  if (!open) return null;

  const inline = {
    ...style,
    // Off-screen until measured, so nothing flashes at (0,0).
    top: pos ? pos.top : -9999,
    left: pos ? pos.left : -9999,
    ...(pos && pos.maxHeight ? { maxHeight: pos.maxHeight, overflowY: "auto" } : {}),
  };

  return (
    <ModalPortal>
      <div ref={popRef} className={`ds-popover ${className || ""}`.trim()} style={inline} {...rest}>
        {children}
      </div>
    </ModalPortal>
  );
};

AnchoredPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  onClose: PropTypes.func.isRequired,
  align: PropTypes.oneOf(["left", "right"]),
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};

AnchoredPopover.defaultProps = {
  align: "right",
  className: "",
  style: undefined,
  children: null,
};

export default AnchoredPopover;
